/**
 * Format a currency value with an ISO 4217 code (e.g. "EUR", "USD", "GBP").
 *
 * Uses `Intl.NumberFormat` so the grouping and decimal separators follow the
 * currency's native locale (USD → en-US, EUR → de-DE, GBP → en-GB, INR →
 * en-IN, …) rather than whatever locale the browser happens to be set to.
 * This keeps a USD amount rendering as "$360,000.00" and a EUR amount as
 * "360.000,00 €" regardless of the viewer's OS locale.
 *
 * When the code is missing, falls back to a plain number format without a
 * symbol — never hardcode a default currency here. Callers can pass an
 * explicit `locale` to override the currency-derived default.
 */

import { separatorsForLocale, type NumberSeparators } from '@/lib/format/number';

// ISO 4217 currency code → canonical BCP-47 locale used for grouping/decimals.
// Extend as new currencies are supported. Unknown codes fall through to en-US.
const CURRENCY_LOCALE: Readonly<Record<string, string>> = {
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  INR: 'en-IN',
  JPY: 'ja-JP',
  CNY: 'zh-CN',
  CHF: 'de-CH',
  CAD: 'en-CA',
  AUD: 'en-AU',
  NZD: 'en-NZ',
  SGD: 'en-SG',
  HKD: 'en-HK',
  KRW: 'ko-KR',
  BRL: 'pt-BR',
  MXN: 'es-MX',
  ZAR: 'en-ZA',
  AED: 'ar-AE',
  SAR: 'ar-SA',
  SEK: 'sv-SE',
  NOK: 'nb-NO',
  DKK: 'da-DK',
  PLN: 'pl-PL',
  CZK: 'cs-CZ',
  HUF: 'hu-HU',
  TRY: 'tr-TR',
  RUB: 'ru-RU',
  THB: 'th-TH',
  IDR: 'id-ID',
  MYR: 'ms-MY',
  PHP: 'en-PH',
  VND: 'vi-VN',
};

// Fallback locale used only when no currency code is available and the caller
// passes no explicit locale. Kept as en-US so bare numeric cells still render
// with predictable grouping instead of following the browser's OS locale.
export const FALLBACK_LOCALE = 'en-US';

/** ISO 4217 codes the formatter has a display locale for, in table order. */
export const SUPPORTED_CURRENCY_CODES: readonly string[] = Object.keys(CURRENCY_LOCALE);

export function localeForCurrency(currencyCode: string | null | undefined): string {
  const code = currencyCode?.trim().toUpperCase();
  if (!code) return FALLBACK_LOCALE;
  return CURRENCY_LOCALE[code] ?? FALLBACK_LOCALE;
}

export function formatCurrency(
  value: number,
  currencyCode: string | null | undefined,
  locale?: string,
): string {
  const code = currencyCode?.trim().toUpperCase();
  const resolvedLocale = locale ?? localeForCurrency(code);
  if (code) {
    try {
      return new Intl.NumberFormat(resolvedLocale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      // Intl throws RangeError on invalid currency codes — fall through.
    }
  }
  return value.toLocaleString(resolvedLocale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ---------------------------------------------------------------------------
// Parsing user-typed money
// ---------------------------------------------------------------------------

/** Group and decimal separators the given currency's locale renders with. */
export function separatorsForCurrency(currencyCode: string | null | undefined): NumberSeparators {
  return separatorsForLocale(localeForCurrency(currencyCode));
}

// Symbols that unambiguously identify a currency when typed instead of a code.
const CURRENCY_SYMBOLS: ReadonlyArray<readonly [symbol: string, code: string]> = [
  ['€', 'EUR'],
  ['$', 'USD'],
  ['£', 'GBP'],
  ['¥', 'JPY'],
  ['₹', 'INR'],
  ['₩', 'KRW'],
  ['₺', 'TRY'],
  ['₽', 'RUB'],
  ['₪', 'ILS'],
  ['฿', 'THB'],
  ['₫', 'VND'],
  ['zł', 'PLN'],
];

// A standalone three-letter word such as "EUR" or "usd".
const ISO_CODE_PATTERN = /(?<![A-Za-z])([A-Za-z]{3})(?![A-Za-z])/u;
// Digits with optional sign and dot / comma separators; nothing else.
const NUMERIC_PATTERN = /^[+-]?(?:\d[\d.,]*|[.,]\d+)$/u;
// Whitespace-like grouping characters some locales render (narrow no-break
// space, apostrophe) plus underscores people type as visual separators.
const IGNORED_GROUPING_PATTERN = /[\s\u00A0\u202F'’_]/gu;

export interface ParsedCurrencyInput {
  readonly amount: number;
  readonly currencyCode: string | null;
}

/**
 * Parse money the way people actually type it: `71340000 EUR`, `€ 1.234,56`,
 * `$1,234.56`, `71.340.000`, `2,500`. A code or symbol in the text wins;
 * otherwise `fallbackCurrencyCode` (typically the cell's current currency)
 * is kept so editing an amount never silently drops its currency.
 *
 * Ambiguous separators are resolved by the currency's own locale, matching
 * how the value is displayed to the user: `1.500 EUR` reads as one thousand
 * five hundred, `1.500 USD` as one and a half.
 *
 * Returns `null` when no finite amount can be read.
 */
export function parseCurrencyInput(
  raw: string,
  fallbackCurrencyCode: string | null | undefined,
): ParsedCurrencyInput | null {
  let text = raw.trim();
  if (text === '') return null;

  let currencyCode: string | null = null;
  const codeMatch = ISO_CODE_PATTERN.exec(text);
  const matchedCode = codeMatch?.[1];
  if (codeMatch !== null && matchedCode !== undefined) {
    currencyCode = matchedCode.toUpperCase();
    text = text.replace(codeMatch[0], ' ');
  } else {
    for (const [symbol, code] of CURRENCY_SYMBOLS) {
      if (text.includes(symbol)) {
        currencyCode = code;
        text = text.split(symbol).join(' ');
        break;
      }
    }
  }
  if (currencyCode === null) {
    const fallback = fallbackCurrencyCode?.trim().toUpperCase();
    currencyCode = fallback !== undefined && fallback !== '' ? fallback : null;
  }

  const numericText = text.replace(IGNORED_GROUPING_PATTERN, '');
  if (!NUMERIC_PATTERN.test(numericText)) return null;
  const amount = parseLocalizedNumber(numericText, separatorsForCurrency(currencyCode));
  return amount === null ? null : { amount, currencyCode };
}

function parseLocalizedNumber(text: string, separators: NumberSeparators): number | null {
  const hasDot = text.includes('.');
  const hasComma = text.includes(',');
  let normalized = text;
  if (hasDot && hasComma) {
    // With both present the last separator is the decimal point.
    const decimal = text.lastIndexOf('.') > text.lastIndexOf(',') ? '.' : ',';
    const group = decimal === '.' ? ',' : '.';
    normalized = text.split(group).join('').replace(decimal, '.');
  } else if (hasDot || hasComma) {
    const separator = hasDot ? '.' : ',';
    const occurrences = text.split(separator).length - 1;
    const digitsAfter = text.length - text.lastIndexOf(separator) - 1;
    // Repeated separators are always grouping. A single separator followed by
    // exactly three digits is grouping only when the locale groups with it.
    const isGrouping =
      occurrences > 1 || (digitsAfter === 3 && separators.group === separator);
    normalized = isGrouping ? text.split(separator).join('') : text.replace(separator, '.');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}
